import { useMemo, useState } from "react";
import { ApplicationIcon } from "../components/ApplicationIcon";

const WORKSPACE_GROUP_ORDER = ["Command", "Daily operations", "Chapters and programs", "Members and programs", "Business", "Setup"];
const WORKSPACE_MENU_TABS = [
  { id: "applications", label: "Applications" },
  { id: "settings", label: "App Settings" }
];

export function ApplicationsPage({
  applications,
  catalogLoading,
  disableApplication,
  disablingApplicationId,
  enableApplication,
  enablingApplicationId,
  launcherItems = [],
  openModule,
  ownerLoading = false,
  rangeApplications,
  rangeApplicationsLoading,
  selectedRange
}) {
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState("applications");
  const enabledByApplicationId = useMemo(
    () => new Map(rangeApplications.map((item) => [item.applicationId, item])),
    [rangeApplications]
  );
  const launcherRows = useMemo(() => launcherItems.map((application) => {
    const enabledRecord = application.applicationId
      ? enabledByApplicationId.get(application.applicationId)
      : null;
    const isEnabled = application.enabled || enabledRecord?.status === "enabled";
    const isPreview = application.status === "preview";
    return {
      ...application,
      isEnabled,
      isPreview
    };
  }), [enabledByApplicationId, launcherItems]);
  const enabledLauncherRows = useMemo(
    () => launcherRows.filter((application) => application.isEnabled),
    [launcherRows]
  );
  const visibleLauncherRows = activeWorkspaceTab === "applications"
    ? enabledLauncherRows
    : launcherRows;
  const groupedLauncherItems = useMemo(() => (
    WORKSPACE_GROUP_ORDER
      .map((group) => ({
        group,
        items: visibleLauncherRows.filter((item) => item.category === group)
      }))
      .filter((group) => group.items.length > 0)
  ), [visibleLauncherRows]);
  const workspaceSummary = useMemo(() => {
    return launcherRows.reduce((summary, application) => {
      if (application.isEnabled) {
        summary.enabled += 1;
      } else if (application.isPreview) {
        summary.preview += 1;
      } else {
        summary.available += 1;
      }

      return summary;
    }, { enabled: 0, available: 0, preview: 0 });
  }, [launcherRows]);

  if (!selectedRange) {
    return (
      <section className="panel next-step-panel">
        <div className="widget-title-row">
          <div className="panel-header">
            <h2>Workspace Menu</h2>
            <p>
              {ownerLoading
                ? "Loading the active workspace."
                : "Create a workspace first."}
            </p>
          </div>
        </div>
        <div className="empty-state">{ownerLoading ? "Loading workspace menu..." : "Waiting for an active range."}</div>
      </section>
    );
  }

  return (
    <section className="panel next-step-panel" id="applications">
      <div className="widget-title-row">
        <div className="panel-header">
          <h2>Workspace Menu</h2>
          <p>{activeWorkspaceTab === "applications" ? "Open enabled applications for" : "Enable or disable applications for"} {selectedRange.name}.</p>
        </div>
        <div className="workspace-menu-summary" aria-label="Workspace menu summary">
          <span><strong>{workspaceSummary.enabled}</strong> Enabled</span>
          <span><strong>{workspaceSummary.available}</strong> Available</span>
          {workspaceSummary.preview > 0 && <span><strong>{workspaceSummary.preview}</strong> Preview</span>}
        </div>
      </div>

      <div className="workspace-menu-tabs" role="tablist" aria-label="Workspace Menu views">
        {WORKSPACE_MENU_TABS.map((tab) => (
          <button
            aria-controls={`workspace-menu-panel-${tab.id}`}
            aria-selected={activeWorkspaceTab === tab.id}
            className={activeWorkspaceTab === tab.id ? "is-active" : ""}
            key={tab.id}
            onClick={() => setActiveWorkspaceTab(tab.id)}
            role="tab"
            type="button"
          >
            <span>{tab.label}</span>
            <strong>{tab.id === "applications" ? workspaceSummary.enabled : launcherRows.length}</strong>
          </button>
        ))}
      </div>

      {(catalogLoading || rangeApplicationsLoading) && (
        <div className="empty-state">Loading workspace menu...</div>
      )}

      {!catalogLoading && applications.length === 0 && launcherItems.length === 0 && (
        <div className="empty-state">No applications are available yet. Seed the shared catalog first.</div>
      )}

      {!catalogLoading && !rangeApplicationsLoading && activeWorkspaceTab === "applications" && enabledLauncherRows.length === 0 && (
        <div className="empty-state workspace-menu-empty">
          <strong>No applications enabled yet.</strong>
          <button type="button" onClick={() => setActiveWorkspaceTab("settings")}>Open App Settings</button>
        </div>
      )}

      <div
        className="catalog-preview workspace-menu-preview"
        id={`workspace-menu-panel-${activeWorkspaceTab}`}
        role="tabpanel"
      >
        {groupedLauncherItems.map(({ group, items }) => (
          <section className="workspace-menu-group" key={group}>
            <div className="workspace-menu-group-heading">
              <span>{group}</span>
              <strong>{items.length} work area{items.length === 1 ? "" : "s"}</strong>
            </div>
            <div className="workspace-menu-list">
        {items.map((application) => {
          const isEnabled = application.isEnabled;
          const isPreview = application.isPreview;
          const isEnabling = application.applicationId && enablingApplicationId === application.applicationId;
          const isDisabling = application.applicationId && disablingApplicationId === application.applicationId;
          const stateLabel = isEnabled ? "Enabled" : isPreview ? "Preview" : "Available";
          const showSettingsControls = activeWorkspaceTab === "settings";

          return (
            <article className={`catalog-card ${isEnabled ? "is-enabled" : ""}`} key={application.key || application.applicationId || application.label}>
              <div className="catalog-card-top">
                <ApplicationIcon appKey={application.iconKey || application.moduleKey} />
                <div>
                  <strong>{application.label || application.name}</strong>
                  <span>{application.category}</span>
                </div>
              </div>
              <p>{application.description}</p>
              <div className={`catalog-card-actions ${isEnabled ? "is-enabled" : "is-available"}`}>
                <span className={`app-state ${isEnabled ? "is-enabled" : isPreview ? "is-preview" : ""}`}>
                  {stateLabel}
                </span>
                {isEnabled ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (application.path) {
                        openModule(application.moduleKey, application.path);
                      } else {
                        openModule(application.moduleKey);
                      }
                    }}
                  >
                    Open
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={isEnabling || !application.applicationId}
                    onClick={() => enableApplication(application.applicationId)}
                  >
                    {isEnabling ? "Enabling..." : "Enable"}
                  </button>
                )}
                {showSettingsControls && isEnabled && (
                  <button
                    type="button"
                    className="secondary-button catalog-disable-button"
                    disabled={isDisabling}
                    onClick={() => disableApplication(application.applicationId)}
                  >
                    {isDisabling ? "Disabling..." : "Disable"}
                  </button>
                )}
              </div>
            </article>
          );
        })}
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}
